import { useTranslation } from 'react-i18next';
import {
  AsyncSelect,
  CreatableAsyncSelect,
  Form,
  SELECT_OTHER_VALUE_PREFIX,
  TableColumnType,
} from '@/components';
import { useMemo } from 'react';
import {
  CostEstimateInput,
  FlatRow,
  HomeInput,
  ImportTasksFormValue,
  NameInput,
  parseResponsiblePersonValue,
  useHomeLookupSource,
  useResponsiblePersonsLookupSource,
} from '@/views';
import {
  CHECKLIST_TASK_STATUS_VALUES,
  INSPECTION_TASK_STATUS_VALUES,
  TaskStatus,
  TaskType,
} from '@/core';
import { ResponsiblePersonInput } from '@/views/Tasks.Details/ImportTasks/Fields/ResponsiblePersonInput';
import { DatePicker, Input, InputNumber, Select, Typography } from 'antd';
import { useFormikContext } from 'formik';
import { DATE_FORMAT_MOMENT } from '@/platform';
import { enumValues, MAX_MONEY_VALUE } from '@/utils';
import { useEnumTranslation } from '@/utils/useEnumTranslation';

const ColumnTitle = (props: { label: string; required?: boolean }) => {
  const { label, required } = props;
  const { Text } = Typography;
  return (
    <Text>
      {label} {required && <Text type="warning">*</Text>}
    </Text>
  );
};

const StatusCell = ({ index }: { index: number }) => {
  const { values } = useFormikContext<ImportTasksFormValue>();
  const currentType = values.rows?.[index]?.type;

  return (
    <Form.EnumSelect
      name={`rows.${index}.status`}
      type={TaskStatus}
      values={
        currentType === TaskType.Checklist
          ? CHECKLIST_TASK_STATUS_VALUES
          : INSPECTION_TASK_STATUS_VALUES
      }
      label={false}
    />
  );
};

const HeaderStatusSelect = ({
  row,
  onChange,
}: {
  row: FlatRow;
  onChange: (value: TaskStatus) => void;
}) => {
  const { values } = useFormikContext<ImportTasksFormValue>();

  const firstChildIndex = row.children?.[0]?.flatIndexInRows;
  const currentType: TaskType | undefined =
    typeof firstChildIndex === 'number'
      ? values.rows?.[firstChildIndex]?.type
      : undefined;

  const statusOptions = useStatusOptions(currentType);

  return (
    <Select
      key={`header-status-${row.key}-${currentType}`}
      options={statusOptions}
      onChange={onChange}
      className="w-100"
    />
  );
};

function useStatusOptions(type?: TaskType) {
  const et = useEnumTranslation();

  return useMemo(() => {
    const enumVals =
      type === TaskType.Checklist
        ? CHECKLIST_TASK_STATUS_VALUES
        : INSPECTION_TASK_STATUS_VALUES;

    return enumVals.map((x) => ({ value: x, label: et(TaskStatus, x) }));
  }, [et, type]);
}

function useTypeOptions() {
  const et = useEnumTranslation();

  return useMemo(() => {
    const enumVals = enumValues(TaskType);
    return enumVals.map((x) => ({ value: x, label: et(TaskType, x) }));
  }, [et]);
}

export function useImportTaskTemplateColumns(
  communityId: string,
  projectId: string,
) {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext<ImportTasksFormValue>();
  const responsiblePersonDataSource =
    useResponsiblePersonsLookupSource(communityId);
  const homeDataSource = useHomeLookupSource(communityId, projectId);
  const typeOptions = useTypeOptions();

  const updateTasksValues = (row: FlatRow, value: any, fieldName: string) => {
    row.children?.forEach((child) => {
      setFieldValue(`rows.${child.flatIndexInRows}.${fieldName}`, value);
    });
  };

  return useMemo<TableColumnType<FlatRow>[]>(() => {
    const columns: Array<TableColumnType<FlatRow>> = [
      {
        key: 'expanded',
        width: '50px',
      },
      {
        title: t('tasks.templateName'),
        dataIndex: 'templateName',
        key: 'templateName',
        render: (value, row) =>
          row.isHeader ? <strong>{value}</strong> : null,
      },
      {
        title: <ColumnTitle label={t('tasks.name')} required />,
        dataIndex: 'name',
        key: 'name',
        render: (_, row) =>
          row.isHeader ? (
            <Input
              onChange={(e) => updateTasksValues(row, e.target.value, 'name')}
            />
          ) : (
            <NameInput name={`rows.${row.flatIndexInRows}`} />
          ),
      },
      {
        title: <ColumnTitle label={t('tasks.home')} required />,
        key: 'home',
        render: (_, row) =>
          row.isHeader ? (
            <AsyncSelect
              onChange={(value) => updateTasksValues(row, value, 'homeId')}
              value={undefined}
              dataSource={homeDataSource}
            />
          ) : (
            <HomeInput
              communityId={communityId}
              projectId={projectId}
              name={`rows.${row.flatIndexInRows}`}
            />
          ),
      },
      {
        title: t('tasks.startDate'),
        key: 'startDate',
        render: (_, row) =>
          row.isHeader ? (
            <DatePicker
              onChange={(value) => {
                updateTasksValues(
                  row,
                  value?.utc(true).startOf('date') ?? null,
                  'startDate',
                );
              }}
              format={DATE_FORMAT_MOMENT}
              className="w-100"
            />
          ) : (
            <Form.Date
              name={`rows.${row.flatIndexInRows}.startDate`}
              label={false}
            />
          ),
      },
      {
        title: t('tasks.endDate'),
        key: 'endDate',
        render: (_, row) =>
          row.isHeader ? (
            <DatePicker
              onChange={(value) => {
                updateTasksValues(
                  row,
                  value?.utc(true).startOf('date') ?? null,
                  'endDate',
                );
              }}
              format={DATE_FORMAT_MOMENT}
              className="w-100"
            />
          ) : (
            <Form.Date
              name={`rows.${row.flatIndexInRows}.endDate`}
              label={false}
            />
          ),
      },
      {
        title: <ColumnTitle label={t('tasks.responsiblePerson')} />,
        key: 'responsiblePerson',
        render: (_, row) =>
          row.isHeader ? (
            <CreatableAsyncSelect
              dataSource={responsiblePersonDataSource}
              value={''}
              onChange={(value) =>
                updateTasksValues(
                  row,
                  {
                    value: value,
                    label: value?.startsWith(SELECT_OTHER_VALUE_PREFIX)
                      ? parseResponsiblePersonValue(value).otherValue
                      : parseResponsiblePersonValue(value).label,
                  },
                  'responsiblePerson',
                )
              }
            />
          ) : (
            <ResponsiblePersonInput
              communityId={communityId}
              name={`rows.${row.flatIndexInRows}`}
              initialValue={row.responsiblePerson}
            />
          ),
      },
      {
        title: t('tasks.costEstimated'),
        key: 'estimatedCost',
        render: (_, row) =>
          row.isHeader ? (
            <InputNumber
              min={0}
              max={MAX_MONEY_VALUE}
              prefix="$"
              onChange={(value) =>
                updateTasksValues(row, value, 'estimatedCost')
              }
              className="w-100"
            />
          ) : (
            <CostEstimateInput name={`rows.${row.flatIndexInRows}`} />
          ),
      },
      {
        title: <ColumnTitle label={t('tasks.type')} required />,
        key: 'type',
        render: (_, row) =>
          row.isHeader ? (
            <Select
              options={typeOptions}
              onChange={(value: TaskType) => {
                updateTasksValues(row, value, 'type');
                updateTasksValues(row, TaskStatus.Incomplete, 'status');
              }}
              className="w-100"
            />
          ) : (
            <Form.EnumSelect
              type={TaskType}
              name={`rows.${row.flatIndexInRows}.type`}
              label={false}
            />
          ),
      },
      {
        title: <ColumnTitle label={t('tasks.status')} required />,
        key: 'status',
        render: (_, row) => {
          return row.isHeader ? (
            <HeaderStatusSelect
              row={row}
              onChange={(value: TaskStatus) =>
                updateTasksValues(row, value, 'status')
              }
            />
          ) : (
            <StatusCell index={row.flatIndexInRows!} />
          );
        },
      },
      {
        title: 'checked',
        key: 'checked',
        render: (_, row) => (
          <Form.Checkbox name={`rows.${row.flatIndexInRows}.checked`} />
        ),
      },
    ];

    return columns.filter(
      (x) => !!x && x.key !== 'checked',
    ) as TableColumnType<FlatRow>[];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, homeDataSource, communityId, projectId, responsiblePersonDataSource]);
}
